// frontend/src/components/admin/LoginConfigManager.jsx
// ============================================================================
// GESTIONNAIRE CONFIG PAGE DE CONNEXION — v2
// Ajout : copyright_text, popup_aide, popup_cgu, popup_confidentialite
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Type, Megaphone, Image, Link2, ExternalLink, Plus, Trash2,
  Save, RefreshCw, ChevronDown, ChevronUp, GripVertical,
  CheckCircle2, AlertCircle, Upload, Eye, X, FileText,
  Copyright, HelpCircle, Shield, BookOpen,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// SECTION WRAPPER
// ============================================================================
const ConfigSection = ({ title, icon: Icon, description, children, isOpen, onToggle, badge }) => {
  const t = useTheme();
  return (
    <div className="rounded-xl border overflow-hidden transition-all" style={{ borderColor: t.border, backgroundColor: t.bgCard }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors"
        style={{ backgroundColor: isOpen ? t.primarySofter : 'transparent' }}
        onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = t.bgCardHover; }}
        onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: t.primarySoft }}>
          <Icon size={16} style={{ color: t.primaryText }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold" style={{ color: t.text }}>{title}</span>
            {badge && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: t.primarySoft, color: t.primaryText }}>
                {badge}
              </span>
            )}
          </div>
          <span className="text-xs" style={{ color: t.textMuted }}>{description}</span>
        </div>
        <ChevronDown size={16} style={{ color: t.textMuted }} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: t.borderLight, animation: 'hpFadeIn 0.15s ease-out' }}>
          <div className="pt-4">{children}</div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ITEM EDITOR
// ============================================================================
const ItemEditor = ({ item, fields, onChange, onRemove, index }) => {
  const t = useTheme();
  return (
    <div className="relative rounded-lg border p-4 transition-all group"
      style={{ borderColor: t.borderLight, backgroundColor: t.bgInput }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = t.borderActive}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderLight}
    >
      <div className="flex items-start gap-3">
        <span className="text-[10px] font-bold mt-2 w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: t.primarySoft, color: t.primaryText }}>
          {index + 1}
        </span>
        <div className="flex-1 space-y-3">
          {fields.map(field => (
            <div key={field.key}>
              <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1 block" style={{ color: t.textMuted }}>
                {field.label}
              </label>
              {field.type === 'textarea' ? (
                <textarea value={item[field.key] || ''} onChange={(e) => onChange(field.key, e.target.value)}
                  rows={2} className="w-full px-3 py-2 rounded-lg border text-sm outline-none transition-all resize-none"
                  style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
                  onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                  onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
                  placeholder={field.placeholder} />
              ) : (
                <input type="text" value={item[field.key] || ''} onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none transition-all"
                  style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
                  onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                  onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
                  placeholder={field.placeholder} />
              )}
            </div>
          ))}
        </div>
        <button onClick={onRemove}
          className="p-1.5 rounded-lg transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
          style={{ color: t.textMuted }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.dangerSoft; e.currentTarget.style.color = t.dangerText; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = t.textMuted; }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// HERO TEXT EDITOR
// ============================================================================
const HeroTextEditor = ({ item, onChange, onRemove, index }) => {
  const t = useTheme();
  const lines = item.lines || [''];
  const updateLine = (li, value) => { const n = [...lines]; n[li] = value; onChange('lines', n); };
  const addLine = () => onChange('lines', [...lines, '']);
  const removeLine = (li) => { if (lines.length <= 1) return; onChange('lines', lines.filter((_, i) => i !== li)); };

  return (
    <div className="relative rounded-lg border p-4 transition-all group"
      style={{ borderColor: t.borderLight, backgroundColor: t.bgInput }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = t.borderActive}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderLight}
    >
      <div className="flex items-start gap-3">
        <span className="text-[10px] font-bold mt-2 w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: t.primarySoft, color: t.primaryText }}>{index + 1}</span>
        <div className="flex-1 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1 block" style={{ color: t.textMuted }}>Lignes du titre</label>
            <div className="space-y-2">
              {lines.map((line, li) => (
                <div key={li} className="flex items-center gap-2">
                  <input type="text" value={line} onChange={(e) => updateLine(li, e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border text-sm outline-none transition-all"
                    style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
                    onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                    onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
                    placeholder={`Ligne ${li + 1}`} />
                  {lines.length > 1 && (
                    <button onClick={() => removeLine(li)} className="p-1 rounded" style={{ color: t.textMuted }}
                      onMouseEnter={(e) => e.currentTarget.style.color = t.dangerText}
                      onMouseLeave={(e) => e.currentTarget.style.color = t.textMuted}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addLine} className="text-xs font-semibold flex items-center gap-1 transition-colors" style={{ color: t.primaryText }}>
                <Plus size={12} /> Ajouter une ligne
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1 block" style={{ color: t.textMuted }}>Sous-titre</label>
            <textarea value={item.subtitle || ''} onChange={(e) => onChange('subtitle', e.target.value)}
              rows={2} className="w-full px-3 py-2 rounded-lg border text-sm outline-none transition-all resize-none"
              style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
              onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
              onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
              placeholder="Description sous le titre..." />
          </div>
        </div>
        <button onClick={onRemove}
          className="p-1.5 rounded-lg transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
          style={{ color: t.textMuted }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.dangerSoft; e.currentTarget.style.color = t.dangerText; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = t.textMuted; }}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// POPUP EDITOR — éditeur pour titre + contenu d'un pop-up
// ============================================================================
const PopupEditor = ({ value, onChange, icon: Icon, accentColor }) => {
  const t = useTheme();
  return (
    <div className="space-y-4">
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2 block" style={{ color: t.textMuted }}>
          Titre du pop-up
        </label>
        <input type="text" value={value.titre || ''} onChange={(e) => onChange({ ...value, titre: e.target.value })}
          className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-all"
          style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
          onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
          onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
          placeholder="Titre affiché dans le pop-up" />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2 block" style={{ color: t.textMuted }}>
          Contenu (texte libre — les sauts de ligne sont respectés)
        </label>
        <textarea value={value.contenu || ''} onChange={(e) => onChange({ ...value, contenu: e.target.value })}
          rows={10} className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-all"
          style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text, resize: 'vertical', lineHeight: '1.6', fontFamily: 'inherit' }}
          onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
          onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
          placeholder="Contenu du pop-up..." />
        <p className="text-xs mt-1.5" style={{ color: t.textMuted }}>
          Les retours à la ligne sont conservés. Utilisez des titres en MAJUSCULES pour structurer.
        </p>
      </div>
      {/* Aperçu miniature */}
      <div className="rounded-lg border p-4" style={{ borderColor: t.border, background: t.bgInput }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: accentColor + '20' }}>
            <Icon size={13} style={{ color: accentColor }} />
          </div>
          <span className="text-xs font-bold" style={{ color: t.text }}>{value.titre || 'Titre du pop-up'}</span>
        </div>
        <p className="text-xs" style={{ color: t.textMuted, lineHeight: '1.5', whiteSpace: 'pre-line' }}>
          {(value.contenu || '').substring(0, 150)}{(value.contenu || '').length > 150 ? '...' : ''}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const LoginConfigManager = () => {
  const t = useTheme();

  const [heroTexts,          setHeroTexts]          = useState([]);
  const [tickerAds,          setTickerAds]           = useState([]);
  const [featuredAds,        setFeaturedAds]         = useState([]);
  const [footerLinks,        setFooterLinks]         = useState([]);
  const [backgroundImage,    setBackgroundImage]     = useState({ url: '', overlay_opacity: 0.4 });
  const [copyrightText,      setCopyrightText]       = useState('HoraPilot est une suite logicielle éditée par DANICED GROUP · Tous droits de reproduction interdits');
  const [popupAide,          setPopupAide]           = useState({ titre: 'Aide et contact', contenu: '' });
  const [popupCgu,           setPopupCgu]            = useState({ titre: 'Conditions Générales d\'Utilisation', contenu: '' });
  const [popupConfidentialite, setPopupConfidentialite] = useState({ titre: 'Politique de Confidentialité', contenu: '' });

  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState({});
  const [uploading, setUploading] = useState(false);
  const [toast,     setToast]     = useState(null);
  const [openSection, setOpenSection] = useState('hero_texts');

  // ── Charger ──
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.loginConfig.getAll();
      if (res.success && res.data) {
        const d = res.data;
        if (d.hero_texts?.value)           setHeroTexts(d.hero_texts.value);
        if (d.ticker_ads?.value)           setTickerAds(d.ticker_ads.value);
        if (d.featured_ads?.value)         setFeaturedAds(d.featured_ads.value);
        if (d.footer_links?.value)         setFooterLinks(d.footer_links.value);
        if (d.background_image?.value)     setBackgroundImage(d.background_image.value);
        if (d.copyright_text?.value)       setCopyrightText(d.copyright_text.value);
        if (d.popup_aide?.value)           setPopupAide(d.popup_aide.value);
        if (d.popup_cgu?.value)            setPopupCgu(d.popup_cgu.value);
        if (d.popup_confidentialite?.value) setPopupConfidentialite(d.popup_confidentialite.value);
      }
    } catch (err) {
      showToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const saveSection = async (key, value) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await superAdminApi.loginConfig.update(key, value);
      if (res.success) showToast(`Sauvegardé ✓`);
      else showToast(res.message || 'Erreur de sauvegarde', 'error');
    } catch { showToast('Erreur de sauvegarde', 'error'); }
    finally { setSaving(prev => ({ ...prev, [key]: false })); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showToast('Max 5 Mo', 'error'); return; }
    setUploading(true);
    try {
      const res = await superAdminApi.loginConfig.uploadBackground(file);
      if (res.success) { setBackgroundImage(prev => ({ ...prev, url: res.data.url })); showToast('Image mise à jour'); }
      else showToast(res.message || 'Erreur upload', 'error');
    } catch { showToast('Erreur upload', 'error'); }
    finally { setUploading(false); }
  };

  const updateListItem = (setter, index, key, value) => setter(prev => prev.map((item, i) => i === index ? { ...item, [key]: value } : item));
  const removeListItem = (setter, index) => setter(prev => prev.filter((_, i) => i !== index));
  const addListItem    = (setter, template) => setter(prev => [...prev, template]);

  const SaveButton = ({ sectionKey, value }) => (
    <button onClick={() => saveSection(sectionKey, value)} disabled={saving[sectionKey]}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all mt-4"
      style={{ backgroundColor: t.primarySoft, borderColor: t.primaryBorder, color: t.primaryText, opacity: saving[sectionKey] ? 0.6 : 1 }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.primary; e.currentTarget.style.color = '#fff'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.primarySoft; e.currentTarget.style.color = t.primaryText; }}>
      {saving[sectionKey] ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
      {saving[sectionKey] ? 'Sauvegarde...' : 'Sauvegarder'}
    </button>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw size={24} className="animate-spin mx-auto mb-3" style={{ color: t.primary }} />
    </div>
  );

  return (
    <div className="space-y-5" style={{ animation: 'hpFadeIn 0.2s ease-out' }}>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-bold tracking-tight mb-1" style={{ color: t.text }}>Configuration page de connexion</h2>
          <p className="text-sm font-medium" style={{ color: t.textSecondary }}>Gérez les contenus de la page de connexion HoraPilot</p>
        </div>
        <button onClick={loadConfig} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all"
          style={{ borderColor: t.border, color: t.textSecondary }}>
          <RefreshCw size={13} /> Recharger
        </button>
      </div>

      {/* ─── IMAGE DE FOND ─── */}
      <ConfigSection title="Image de fond" icon={Image} description="Image d'arrière-plan de la page de connexion"
        isOpen={openSection === 'background'} onToggle={() => setOpenSection(openSection === 'background' ? null : 'background')}>
        <div className="flex flex-col lg:flex-row gap-5">
          <div className="w-full lg:w-64 h-40 rounded-lg overflow-hidden border relative flex-shrink-0" style={{ borderColor: t.border }}>
            {backgroundImage.url ? <img src={backgroundImage.url} alt="Background" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: t.bgInput }}><Image size={32} style={{ color: t.textMuted }} /></div>}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.4), rgba(0,0,0,0.1))' }} />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2 block" style={{ color: t.textMuted }}>Changer l'image</label>
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border cursor-pointer transition-all"
                style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.textSecondary }}>
                {uploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Upload en cours...' : 'Choisir une image'}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
            <p className="text-xs" style={{ color: t.textMuted }}>JPG, PNG, WebP · Max 5 Mo · 1920×1080 recommandé</p>
          </div>
        </div>
      </ConfigSection>

      {/* ─── HERO TEXTS ─── */}
      <ConfigSection title="Textes Hero" icon={Type} description="Textes animés en rotation côté gauche"
        badge={`${heroTexts.length} texte${heroTexts.length > 1 ? 's' : ''}`}
        isOpen={openSection === 'hero_texts'} onToggle={() => setOpenSection(openSection === 'hero_texts' ? null : 'hero_texts')}>
        <div className="space-y-3">
          {heroTexts.map((item, i) => (
            <HeroTextEditor key={i} item={item} index={i}
              onChange={(key, value) => updateListItem(setHeroTexts, i, key, value)}
              onRemove={() => removeListItem(setHeroTexts, i)} />
          ))}
          <button onClick={() => addListItem(setHeroTexts, { lines: ['Nouveau', 'Titre'], color: '#fdfcf5', subtitle: 'Description ici...' })}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed text-sm font-semibold transition-colors"
            style={{ borderColor: t.border, color: t.textMuted }}>
            <Plus size={16} /> Ajouter un texte hero
          </button>
          <SaveButton sectionKey="hero_texts" value={heroTexts} />
        </div>
      </ConfigSection>

      {/* ─── TICKER ADS ─── */}
      <ConfigSection title="Bandeau défilant" icon={Megaphone} description="Publicités dans le bandeau en haut"
        badge={`${tickerAds.length}`}
        isOpen={openSection === 'ticker_ads'} onToggle={() => setOpenSection(openSection === 'ticker_ads' ? null : 'ticker_ads')}>
        <div className="space-y-3">
          {tickerAds.map((item, i) => (
            <ItemEditor key={i} item={item} index={i}
              fields={[
                { key: 'company', label: 'Entreprise', placeholder: 'Nom du partenaire' },
                { key: 'tagline', label: 'Slogan', placeholder: "Phrase d'accroche..." },
                { key: 'url', label: 'URL', placeholder: 'https://...' },
              ]}
              onChange={(key, value) => updateListItem(setTickerAds, i, key, value)}
              onRemove={() => removeListItem(setTickerAds, i)} />
          ))}
          <button onClick={() => addListItem(setTickerAds, { company: '', tagline: '', url: '' })}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed text-sm font-semibold transition-colors"
            style={{ borderColor: t.border, color: t.textMuted }}>
            <Plus size={16} /> Ajouter un partenaire
          </button>
          <SaveButton sectionKey="ticker_ads" value={tickerAds} />
        </div>
      </ConfigSection>

      {/* ─── FEATURED ADS ─── */}
      <ConfigSection title="Cartes sponsorisées" icon={ExternalLink} description="Publicités en carte côté gauche (rotation automatique)"
        badge={`${featuredAds.length}`}
        isOpen={openSection === 'featured_ads'} onToggle={() => setOpenSection(openSection === 'featured_ads' ? null : 'featured_ads')}>
        <div className="space-y-3">
          {featuredAds.map((item, i) => (
            <ItemEditor key={i} item={item} index={i}
              fields={[
                { key: 'company', label: 'Entreprise', placeholder: 'Nom du sponsor' },
                { key: 'tagline', label: 'Description', type: 'textarea', placeholder: 'Texte publicitaire...' },
                { key: 'cta', label: 'Bouton CTA', placeholder: 'Découvrir, En savoir plus...' },
                { key: 'url', label: 'URL', placeholder: 'https://...' },
              ]}
              onChange={(key, value) => updateListItem(setFeaturedAds, i, key, value)}
              onRemove={() => removeListItem(setFeaturedAds, i)} />
          ))}
          <button onClick={() => addListItem(setFeaturedAds, { company: '', tagline: '', cta: 'Découvrir', url: '' })}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed text-sm font-semibold transition-colors"
            style={{ borderColor: t.border, color: t.textMuted }}>
            <Plus size={16} /> Ajouter une publicité
          </button>
          <SaveButton sectionKey="featured_ads" value={featuredAds} />
        </div>
      </ConfigSection>

      {/* ─── FOOTER LINKS ─── */}
      <ConfigSection title="Liens du footer" icon={Link2} description="Liens dans la barre jaune en bas de la page"
        badge={`${footerLinks.length}`}
        isOpen={openSection === 'footer_links'} onToggle={() => setOpenSection(openSection === 'footer_links' ? null : 'footer_links')}>
        <div className="space-y-3">
          {footerLinks.map((item, i) => (
            <div key={i} className="relative rounded-lg border p-4 transition-all group"
              style={{ borderColor: t.borderLight, backgroundColor: t.bgInput }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = t.borderActive}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderLight}>
              <div className="flex items-start gap-3">
                <span className="text-[10px] font-bold mt-2 w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: t.primarySoft, color: t.primaryText }}>{i + 1}</span>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1 block" style={{ color: t.textMuted }}>Label</label>
                    <input type="text" value={item.label || ''} onChange={(e) => updateListItem(setFooterLinks, i, 'label', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                      style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
                      onFocus={(e) => e.target.style.borderColor = t.borderActive}
                      onBlur={(e) => e.target.style.borderColor = t.border}
                      placeholder="Texte du lien" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1 block" style={{ color: t.textMuted }}>URL ou #popup</label>
                    <input type="text" value={item.url || ''} onChange={(e) => updateListItem(setFooterLinks, i, 'url', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                      style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
                      onFocus={(e) => e.target.style.borderColor = t.borderActive}
                      onBlur={(e) => e.target.style.borderColor = t.border}
                      placeholder="#aide | #cgu | #confidentialite | https://..." />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-6 flex-shrink-0">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: t.textMuted }}>
                    <input type="checkbox" checked={item.external || false}
                      onChange={(e) => updateListItem(setFooterLinks, i, 'external', e.target.checked)}
                      style={{ accentColor: t.primary }} />
                    Externe
                  </label>
                  <button onClick={() => removeListItem(setFooterLinks, i)}
                    className="p-1.5 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                    style={{ color: t.textMuted }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.dangerSoft; e.currentTarget.style.color = t.dangerText; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = t.textMuted; }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <div className="rounded-lg border p-3" style={{ borderColor: t.border, background: t.bgInput }}>
            <p className="text-xs" style={{ color: t.textMuted }}>
              💡 Pour les pop-ups, utilisez <code style={{ color: t.primary }}>#aide</code>, <code style={{ color: t.primary }}>#cgu</code> ou <code style={{ color: t.primary }}>#confidentialite</code> comme URL.
            </p>
          </div>
          <button onClick={() => addListItem(setFooterLinks, { label: '', url: '#', external: false })}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed text-sm font-semibold transition-colors"
            style={{ borderColor: t.border, color: t.textMuted }}>
            <Plus size={16} /> Ajouter un lien
          </button>
          <SaveButton sectionKey="footer_links" value={footerLinks} />
        </div>
      </ConfigSection>

      {/* ─── COPYRIGHT ─── */}
      <ConfigSection title="Mention légale (copyright)" icon={Copyright}
        description="Bande de copyright affichée au-dessus du footer jaune"
        isOpen={openSection === 'copyright'} onToggle={() => setOpenSection(openSection === 'copyright' ? null : 'copyright')}>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2 block" style={{ color: t.textMuted }}>
              Texte de la mention légale
            </label>
            <input type="text" value={copyrightText} onChange={(e) => setCopyrightText(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none"
              style={{ backgroundColor: t.bgInputFocus, borderColor: t.border, color: t.text }}
              onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
              onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
              placeholder="HoraPilot est une suite logicielle éditée par DANICED GROUP · Tous droits réservés" />
          </div>
          {/* Aperçu */}
          <div className="rounded-lg overflow-hidden border" style={{ borderColor: t.border }}>
            <div className="px-4 py-2 text-center text-xs font-medium" style={{ background: '#1a1a1a', color: 'rgba(255,255,255,0.6)' }}>
              © {new Date().getFullYear()} · {copyrightText}
            </div>
            <div className="px-4 py-2.5 flex items-center justify-center gap-3" style={{ background: '#fed774' }}>
              <span className="text-xs font-semibold text-gray-700">Aide et contact</span>
              <span className="text-gray-400 text-xs">|</span>
              <span className="text-xs font-semibold text-gray-700">Conditions d'utilisation</span>
              <span className="text-gray-400 text-xs">|</span>
              <span className="text-xs font-semibold text-gray-700">Politique de confidentialité</span>
            </div>
          </div>
          <p className="text-xs" style={{ color: t.textMuted }}>Affiché sur une bande sombre au-dessus de la barre jaune.</p>
          <SaveButton sectionKey="copyright_text" value={copyrightText} />
        </div>
      </ConfigSection>

      {/* ─── POPUP AIDE ─── */}
      <ConfigSection title="Pop-up Aide et contact" icon={HelpCircle}
        description="Contenu affiché quand on clique sur 'Aide et contact'"
        isOpen={openSection === 'popup_aide'} onToggle={() => setOpenSection(openSection === 'popup_aide' ? null : 'popup_aide')}>
        <PopupEditor value={popupAide} onChange={setPopupAide} icon={HelpCircle} accentColor="#448696" />
        <SaveButton sectionKey="popup_aide" value={popupAide} />
      </ConfigSection>

      {/* ─── POPUP CGU ─── */}
      <ConfigSection title="Pop-up Conditions d'utilisation" icon={BookOpen}
        description="Contenu affiché quand on clique sur 'Conditions d'utilisation'"
        isOpen={openSection === 'popup_cgu'} onToggle={() => setOpenSection(openSection === 'popup_cgu' ? null : 'popup_cgu')}>
        <PopupEditor value={popupCgu} onChange={setPopupCgu} icon={BookOpen} accentColor="#7c3aed" />
        <SaveButton sectionKey="popup_cgu" value={popupCgu} />
      </ConfigSection>

      {/* ─── POPUP CONFIDENTIALITÉ ─── */}
      <ConfigSection title="Pop-up Politique de confidentialité" icon={Shield}
        description="Contenu affiché quand on clique sur 'Politique de confidentialité'"
        isOpen={openSection === 'popup_confidentialite'} onToggle={() => setOpenSection(openSection === 'popup_confidentialite' ? null : 'popup_confidentialite')}>
        <PopupEditor value={popupConfidentialite} onChange={setPopupConfidentialite} icon={Shield} accentColor="#059669" />
        <SaveButton sectionKey="popup_confidentialite" value={popupConfidentialite} />
      </ConfigSection>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border shadow-lg"
          style={{ backgroundColor: toast.type === 'error' ? t.dangerSoft : t.bgCard, borderColor: toast.type === 'error' ? t.dangerText + '30' : t.border, color: toast.type === 'error' ? t.dangerText : t.text, animation: 'hpFadeIn 0.2s ease-out' }}>
          {toast.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} style={{ color: t.success }} />}
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default LoginConfigManager;